/**
 * AppViewModel - Manages UI state and orchestrates interactions between View and Model
 * This ViewModel handles all business logic for the UI, including state management,
 * event handling, and coordination with the data model.
 */

import { useState, useCallback } from 'react';
import OcrModel from '../model/OcrModel';

const CUBE_MOVE_PATTERN = /^(?:[URFDLB](?:w)?(?:2|')?|[xyz](?:2|')?)$/i;
const CUBE_MOVE_GLOBAL_PATTERN = /(?:[URFDLB](?:w)?(?:2|')?|[xyz](?:2|')?)/gi;

/**
 * Main ViewModel hook for the FMC Grader application
 */
export default function useAppViewModel() {
  const [image, setImage] = useState(null);
  const [scramble, setScramble] = useState('');
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit mode state
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');

  // Scramble edit and lock state
  const [editingScramble, setEditingScramble] = useState(false);
  const [scrambleEditValue, setScrambleEditValue] = useState('');
  const [scrambleLocked, setScrambleLocked] = useState(false);

  /**
   * Handle image file selection
   */
  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setImage(file);
    }
  }, []);

  /**
   * Process image through OCR and parse results
   */
  const handleOcr = useCallback(async () => {
    if (!image) return;

    setLoading(true);
    try {
      const { text } = await OcrModel.recognize(image);
      const { scramble: parsedScramble, moves: parsedMoves } = OcrModel.parse(text, scrambleLocked);
      
      // Only update scramble if it's not locked
      if (!scrambleLocked) {
        setScramble(parsedScramble);
      }
      
      setMoves(parsedMoves);
    } catch (error) {
      console.error('OCR processing error:', error);
      alert(`Unable to process image: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [image, scrambleLocked]);

  /**
   * Start editing a move at the specified index
   */
  const startEditingMove = useCallback((index, currentValue) => {
    setEditingIndex(index);
    setEditValue(currentValue);
  }, []);

  /**
   * Cancel the current edit operation
   */
  const cancelEditing = useCallback(() => {
    setEditingIndex(-1);
    setEditValue('');
  }, []);

  /**
   * Parse and validate move tokens from user input
   */
  const parseEditedMove = useCallback((value) => {
    const tokens = (value || '').match(CUBE_MOVE_GLOBAL_PATTERN) || [];
    return tokens.map(token => ({
      text: token,
    }));
  }, []);

  /**
   * Save edited move value and update the moves list
   */
  const saveEditedMove = useCallback((index, value) => {
    const parsedTokens = parseEditedMove(value);

    setMoves(previousMoves => {
      const updatedMoves = [...previousMoves];
      updatedMoves.splice(index, 1, ...parsedTokens);
      return updatedMoves;
    });

    cancelEditing();
  }, [parseEditedMove, cancelEditing]);

  /**
   * Update the edit input value
   */
  const updateEditValue = useCallback((value) => {
    setEditValue(value);
  }, []);

  /**
   * Check if editing mode is active
   */
  const isEditing = useCallback((index) => {
    return editingIndex === index;
  }, [editingIndex]);

  /**
   * Get the total count of moves
   */
  const getMoveCount = useCallback(() => {
    return moves.length;
  }, [moves.length]);

  /**
   * Check if the OCR button should be enabled
   */
  const canProcessImage = useCallback(() => {
    return image !== null && !loading;
  }, [image, loading]);

  /**
   * Get button text based on loading state
   */
  const getProcessButtonText = useCallback(() => {
    return loading ? 'Processing...' : 'Transcribe Moves';
  }, [loading]);

  /**
   * Check if results are available for display
   */
  const hasResults = useCallback(() => {
    return scramble !== '' && moves.length > 0;
  }, [scramble, moves.length]);

  /**
   * Start editing the scramble
   */
  const startEditingScramble = useCallback(() => {
    setEditingScramble(true);
    setScrambleEditValue(scramble);
  }, [scramble]);

  /**
   * Cancel scramble edit
   */
  const cancelEditingScramble = useCallback(() => {
    setEditingScramble(false);
    setScrambleEditValue('');
  }, []);

  /**
   * Save edited scramble
   */
  const saveEditedScramble = useCallback(() => {
    setScramble(scrambleEditValue);
    setEditingScramble(false);
    setScrambleEditValue('');
  }, [scrambleEditValue]);

  /**
   * Update scramble edit value
   */
  const updateScrambleEditValue = useCallback((value) => {
    setScrambleEditValue(value);
  }, []);

  /**
   * Toggle scramble lock state
   */
  const toggleScrambleLock = useCallback(() => {
    setScrambleLocked(prev => !prev);
  }, []);

  return {
    // State
    image,
    scramble,
    moves,
    loading,
    editingIndex,
    editValue,
    editingScramble,
    scrambleEditValue,
    scrambleLocked,

    // Image handling
    handleImageChange,
    handleOcr,

    // Move edit operations
    startEditingMove,
    cancelEditing,
    saveEditedMove,
    updateEditValue,
    isEditing,

    // Scramble edit operations
    startEditingScramble,
    cancelEditingScramble,
    saveEditedScramble,
    updateScrambleEditValue,
    toggleScrambleLock,

    // Computed properties
    getMoveCount,
    canProcessImage,
    getProcessButtonText,
    hasResults
  };
}
